/**
 * SEO utility functions for generating meta tags and structured data
 */

import url from '../Url';

// Base URL for SEO purposes (should always be the production URL for canonical links)
const BASE_URL = "https://hdmovietoday.xyz";

/**
 * Truncates text to specified character limit with ellipsis
 * @param {string} text - Text to truncate
 * @param {number} limit - Character limit
 * @returns {string} - Truncated text
 */
export const truncateText = (text, limit) => {
  if (!text) return "";
  return text.length > limit ? text.substring(0, limit - 3) + "..." : text;
};

/**
 * Generates SEO-friendly page title
 * @param {string} categoryName - Category name
 * @param {string} siteName - Site name
 * @returns {string} - Formatted page title
 */
export const generatePageTitle = (categoryName = "Flooring Products", siteName = "Squarefoot Flooring") => {
  const baseTitle = `${categoryName} | ${siteName}`;
  return truncateText(baseTitle, 60);
};

/**
 * Generates meta description for category pages
 * @param {Object} category - Category object
 * @returns {string} - Meta description
 */
export const generateCategoryDescription = (category) => {
  const categoryName = category?.name || "flooring products";
  const description = category?.description || 
    `Explore our extensive collection of ${categoryName.toLowerCase()} and tiles from the comfort of your home. Quality flooring solutions at competitive prices.`;
  return truncateText(description, 120);
};

/**
 * Generates canonical URL for category pages
 * @param {string} slug - Category slug
 * @returns {string} - Canonical URL
 */
export const generateCanonicalUrl = (slug) => {
  return `${BASE_URL}/category/${slug}`;
};

/**
 * Generates Open Graph image URL
 * @param {Array} products - Array of products
 * @returns {string} - OG image URL
 */
export const generateOgImage = (products = []) => {
  // Try to get the first product image, fallback to default
  const firstProduct = products?.[0];
  if (firstProduct?.img?.length > 0) {
    return firstProduct.img[0];
  }
  // Fallback to a default image - using the 512x512 icon which is better for social sharing
  return `${url}/android-chrome-512x512.png`;
};

/**
 * Generates keywords for category pages
 * @param {Object} category - Category object
 * @returns {string} - Meta keywords
 */
export const generateCategoryKeywords = (category) => {
  if (!category?.name) return "flooring, tiles, home improvement, interior design";
  
  const categoryName = category.name.toLowerCase();
  return `${category.name}, flooring, tiles, ${categoryName} flooring, home improvement, interior design, ${categoryName} tiles`;
};

/**
 * Generates breadcrumb structured data
 * @param {Object} category - Category object
 * @param {string} slug - Category slug
 * @returns {Array} - Breadcrumb list items
 */
export const generateBreadcrumbStructuredData = (category, slug) => {
  const breadcrumbs = [
    {
      "@type": "ListItem",
      "position": 1,
      "name": "Home",
      "item": url
    }
  ];

  // Handle null or undefined category
  if (!category) {
    return breadcrumbs;
  }

  if (category?.Type === "Super_Cat") {
    breadcrumbs.push({
      "@type": "ListItem",
      "position": 2,
      "name": category.name,
      "item": generateCanonicalUrl(slug)
    });
  } else if (category?.Type === "Cat") {
    if (category.parentName) {
      breadcrumbs.push({
        "@type": "ListItem",
        "position": 2,
        "name": category.parentName,
        "item": `${url}/category/${category.parentName?.toLowerCase().replace(/,/g, '').replace(/\s+/g, '-')}`
      });
    }
    breadcrumbs.push({
      "@type": "ListItem",
      "position": breadcrumbs.length + 1,
      "name": category.name,
      "item": generateCanonicalUrl(slug)
    });
  } else {
    // Sub-category
    if (category.superCatName) {
      // Use superCatSlug if available, otherwise generate from name
      const superCatSlug = category.superCatSlug || category.superCatName?.toLowerCase().replace(/,/g, '').replace(/\s+/g, '-');
      breadcrumbs.push({
        "@type": "ListItem",
        "position": 2,
        "name": category.superCatName,
        "item": `${url}/category/${superCatSlug}`
      });
    }
    if (category.parentName) {
      // Use parentSlug if available, otherwise generate from name  
      const parentSlug = category.parentSlug || category.parentName?.toLowerCase().replace(/,/g, '').replace(/\s+/g, '-');
      breadcrumbs.push({
        "@type": "ListItem",
        "position": breadcrumbs.length + 1,
        "name": category.parentName,
        "item": `${url}/category/${parentSlug}`
      });
    }
    breadcrumbs.push({
      "@type": "ListItem",
      "position": breadcrumbs.length + 1,
      "name": category.name,
      "item": generateCanonicalUrl(slug)
    });
  }

  return breadcrumbs;
};

/**
 * Generates product list structured data for category pages
 * @param {Object} category - Category object
 * @param {Array} products - Array of products
 * @param {number} totalProducts - Total number of products
 * @returns {Object} - Product list structured data
 */
export const generateProductListStructuredData = (category, products = [], totalProducts = 0) => {
  return {
    "@type": "ItemList",
    "name": `${category?.name || 'Flooring Products'} Collection`,
    "description": generateCategoryDescription(category),
    "numberOfItems": totalProducts,
    "itemListElement": products?.slice(0, 5)?.map((product, index) => ({
      "@type": "Product",
      "position": index + 1,
      "name": product.name || product.title,
      "description": truncateText(product.shortDescription || product.description, 160),
      "image": product.img?.[0],
      "url": `${url}/product/${product.slug || product.name?.toLowerCase().replace(/\s+/g, '-')}`
    })) || []
  };
};

/**
 * Generates SEO-friendly page title for brand pages
 * @param {string} brandName - Brand name
 * @param {string} siteName - Site name
 * @returns {string} - Formatted page title
 */
export const generateBrandPageTitle = (brandName = "Flooring Brands", siteName = "Squarefoot Flooring") => {
  const baseTitle = `${brandName} Flooring | ${siteName}`;
  return truncateText(baseTitle, 60);
};

/**
 * Generates meta description for brand pages
 * @param {Object} brand - Brand object
 * @returns {string} - Meta description
 */
export const generateBrandDescription = (brand) => {
  const brandName = brand?.brand_name || "premium flooring brand";
  const description = brand?.description || 
    `Discover ${brandName} flooring products and tiles. Quality flooring solutions with competitive prices and professional installation services.`;
  return truncateText(description, 120);
};

/**
 * Generates canonical URL for brand pages
 * @param {string} slug - Brand slug
 * @returns {string} - Canonical URL
 */
export const generateBrandCanonicalUrl = (slug) => {
  return `${url}/brand/${slug}`;
};

/**
 * Generates keywords for brand pages
 * @param {Object} brand - Brand object
 * @returns {string} - Meta keywords
 */
export const generateBrandKeywords = (brand) => {
  if (!brand?.brand_name) return "flooring brands, tiles, home improvement, interior design";
  
  const brandName = brand.brand_name.toLowerCase();
  return `${brand.brand_name}, ${brandName} flooring, ${brandName} tiles, flooring brands, home improvement, interior design`;
};

/**
 * Generates breadcrumb structured data for brand pages
 * @param {Object} brand - Brand object
 * @param {string} slug - Brand slug
 * @param {boolean} isProductPage - Whether this is a brand product page
 * @returns {Array} - Breadcrumb list items
 */
export const generateBrandBreadcrumbStructuredData = (brand, slug, isProductPage = false) => {
  const breadcrumbs = [
    {
      "@type": "ListItem",
      "position": 1,
      "name": "Home",
      "item": url
    }
  ];

  if (isProductPage) {
    breadcrumbs.push({
      "@type": "ListItem",
      "position": 2,
      "name": "Brands",
      "item": `${url}/brands`
    });
    breadcrumbs.push({
      "@type": "ListItem",
      "position": 3,
      "name": brand?.brand_name || "Brand",
      "item": generateBrandCanonicalUrl(slug)
    });
  } else {
    breadcrumbs.push({
      "@type": "ListItem",
      "position": 2,
      "name": brand?.brand_name || "Brands",
      "item": generateBrandCanonicalUrl(slug)
    });
  }

  return breadcrumbs;
};

/**
 * Generates brand organization structured data
 * @param {Object} brand - Brand object
 * @param {string} slug - Brand slug
 * @returns {Object} - Brand organization structured data
 */
export const generateBrandOrganizationStructuredData = (brand, slug) => {
  return {
    "@type": "Organization",
    "name": brand?.brand_name || "Flooring Brand",
    "url": generateBrandCanonicalUrl(slug),
    "logo": brand?.brandLogo,
    "description": generateBrandDescription(brand),
    "sameAs": brand?.website ? [brand.website] : []
  };
};

/**
 * Generates product list structured data for brand pages
 * @param {Object} brand - Brand object
 * @param {Array} products - Array of products
 * @param {number} totalProducts - Total number of products
 * @returns {Object} - Product list structured data
 */
export const generateBrandProductListStructuredData = (brand, products = [], totalProducts = 0) => {
  return {
    "@type": "ItemList",
    "name": `${brand?.brand_name || 'Brand'} Flooring Products`,
    "description": generateBrandDescription(brand),
    "numberOfItems": totalProducts,
    "itemListElement": products?.slice(0, 5)?.map((product, index) => ({
      "@type": "Product",
      "position": index + 1,
      "name": product.name || product.title,
      "description": truncateText(product.shortDescription || product.description, 160),
      "image": product.img?.[0],
      "brand": {
        "@type": "Brand",
        "name": brand?.brand_name
      },
      "url": `${url}/product/${product.slug || product.name?.toLowerCase().replace(/\s+/g, '-')}`
    })) || []
  };
};

/**
 * Generates Open Graph image for brand pages
 * @param {Object} brand - Brand object
 * @param {Array} products - Array of products (optional)
 * @returns {string} - OG image URL
 */
export const generateBrandOgImage = (brand, products = []) => {
  // Try brand logo first
  if (brand?.brandLogo) {
    return brand.brandLogo;
  }
  
  // Try to get the first product image as fallback
  const firstProduct = products?.[0];
  if (firstProduct?.img?.length > 0) {
    return firstProduct.img[0];
  }
  
  // Fallback to default image
  return `${url}/android-chrome-512x512.png`;
};
